import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import { ChevronDownIcon, ExternalLinkIcon, ImageIcon } from 'lucide-react';

import FlexColumn from 'src/components/flex/FlexColumn';
import CopyTextButton from 'src/components/copy-text-button/CopyTextButton';
import { useExpandCollapse } from 'src/components/lead-sidebar/use-expand-collapse';
import { useOneLineOverflow } from 'src/components/lead-sidebar/use-one-line-overflow';

import styles from 'src/components/lead-sidebar/FactRow.module.css';
import highlightStyles from 'src/components/lead-sidebar/just-added-highlight.module.css';

type Props = React.ComponentProps<'div'> & {
    label: string;
    value: string;
    factKey: string;
    /** Copied to the clipboard instead of `value`, e.g. when `value` is truncated for display. */
    copyText?: string;
    /** Optional edit affordance; if provided the value becomes the clickable trigger. */
    editButton?: React.ReactElement<{ children?: React.ReactNode }>;
    /** When true, the row plays a one-shot highlight as it is added to the lead. */
    isJustAdded?: boolean;
    /** When true, `value` is an "Add" prompt for an unset fact, not a real value:
     * it renders muted and there is nothing to copy. */
    isPlaceholder?: boolean;
};

enum FactType {
    IMAGE_LINK,
    LINK,
    STRING,
};

const IMG_EXTENSIONS = new Set(['jpeg', 'jpg', 'png', 'webp']);

/**
 * Infer the type of the fact value based on value contents.
 *
 * NOTE: a known limitation of this method is image detection only works if the link pathname contains an image contains an extension
 * URLs such as https://example.com/images/123 WILL NOT WORK.
 */
const inferType = (value: string): FactType => {
    value = value.trim();
    const isLink = (value.startsWith('https://') || value.startsWith('http://')) && !value.includes(' ');
    if (!isLink) {
        return FactType.STRING;
    }
    try {
        const u = new URL(value);
        // read only pathname, not query params
        const parts = u.pathname.split('.');
        if (parts.length === 1) {
            // no extension
            return FactType.LINK;
        }

        const ext = parts[parts.length - 1].toLowerCase();
        if (IMG_EXTENSIONS.has(ext)) {
            return FactType.IMAGE_LINK;
        } else {
            return FactType.LINK;
        }
    } catch {
        // if new URL fails
        return FactType.STRING;
    }
};

/**
 * Hostname chip. A hostname is a single unbreakable token, so it truncates
 * inside the row instead of pushing the sidebar open — the chip, its flex
 * parent and the row all need `min-width: 0` for that to hold.
 */
function FactLink({ value }: { value: string }) {
    let domain = value;
    try { domain = new URL(value).hostname.replace(/^www\./, ''); } catch { /* keep value */ }

    return (
        <Link
            href={value}
            target="_blank"
            rel="noreferrer noopener"
            referrerPolicy="no-referrer"
            variant="body2"
            className={styles.linkChip}
        >
            <span className={styles.linkChipLabel}>{domain}</span>
            <ExternalLinkIcon size={13} className={styles.externalLinkIcon} aria-hidden />
        </Link>
    );
}

interface IImagePreviewLinkProps {
    value: string;
    isModalOpen: boolean;
    setModalOpen(v: boolean): void;
}

function FactImagePreviewLink({ isModalOpen, setModalOpen }: IImagePreviewLinkProps) {
    const handleClick = (e: React.SyntheticEvent) => {
        e.preventDefault();

        if (!isModalOpen) {
            setModalOpen(true);
        }
    };

    return <Link href="#" onClick={handleClick} className={styles.showImageLink} data-testid="open-fact-image-modal-btn">
        <ImageIcon className={styles.openImagePreviewIcon} size={12} />
        Show
    </Link>;
}

function FactImageModal({ label, value, onClose }: { label: string, value: string, onClose(): void }) {
    return <Dialog open onClose={onClose} className={styles.factImageModal}>
        <DialogTitle>{label} </DialogTitle>
        <DialogContent>
            <FlexColumn width="100%" flexWrap="nowrap">
                <img src={value} alt={label} referrerPolicy="no-referrer" />
            </FlexColumn>
        </DialogContent>
        <DialogActions>
            <Button variant="outlined" color="dark" size="small" onClick={onClose}>
                Close
            </Button>
        </DialogActions>
    </Dialog>;
}

/**
 * One `Label: value` row, the common shape for every fact-like thing in the
 * sidebar.
 *
 * A value that does not fit one line runs to the row edge and dissolves into a
 * right-edge gradient — no ellipsis — and gains a chevron that expands it in
 * place, the full text wrapping after the label. Which shape a value gets is
 * inferred from the value itself plus measurement, never from the fact key, so
 * new fact keys need no wiring here.
 *
 * The actions trail the value: a value that fits keeps its copy icon beside it,
 * while a value that fills the line pushes the icons to the row edge.
 */
export default function FactRow({
    label,
    value,
    factKey,
    copyText,
    editButton,
    isJustAdded = false,
    isPlaceholder = false,
    className,
    ...rowProps
}: Props) {
    const [isImagePreviewOpen, setImagePreviewOpen] = useState<boolean>(false);

    const trimmedValue = value.trim();
    const inferredType = inferType(trimmedValue);
    const isExpandable = inferredType === FactType.STRING;

    const { textRef, isOverflowing } = useOneLineOverflow(trimmedValue);
    const { isExpanded, isSettling, isAnimating, toggle, onTransitionEnd } = useExpandCollapse(textRef);

    /* Collapsing keeps the text wrapped while the height eases down, so nowrap
       and the fade only apply once the row has settled. */
    const isOneLine = !isExpanded && !isSettling;
    const isTruncated = isExpandable && isOneLine && isOverflowing;
    const canEdit = !!editButton && isExpandable;
    const canExpand = isExpandable && (isOverflowing || isExpanded);

    const labelElem = (
        <Typography component="span" variant="body2" color="text.secondary" className={styles.factLabel}>
            {label}:{' '}
        </Typography>
    );

    /* The tooltip is the non-click route to a value the fade cuts off; it goes
       away as soon as the whole value is on screen. */
    const valueElem = (
        <Typography
            component="span"
            variant="body2"
            className={clsx(styles.factValue, canEdit && styles.editableFactValue)}
            color={isPlaceholder ? 'text.secondary' : undefined}
            title={isTruncated ? trimmedValue : undefined}
        >
            {trimmedValue}
        </Typography>
    );

    let valueRegion: React.ReactNode;
    switch (inferredType) {
        case FactType.LINK:
            valueRegion = <FactLink value={trimmedValue} />;
            break;
        case FactType.IMAGE_LINK:
            valueRegion = (
                <FactImagePreviewLink
                    value={trimmedValue}
                    isModalOpen={isImagePreviewOpen}
                    setModalOpen={setImagePreviewOpen}
                />
            );
            break;
        default:
            valueRegion = canEdit ? React.cloneElement(editButton!, {}, valueElem) : valueElem;
            break;
    }

    return (
        <div
            className={clsx(styles.factRow, isJustAdded && highlightStyles.justAddedHighlight, className)}
            {...rowProps}
        >
            {isImagePreviewOpen && (
                <FactImageModal label={label} value={trimmedValue} onClose={() => setImagePreviewOpen(false)} />
            )}
            <Typography
                ref={textRef}
                component="p"
                variant="body2"
                className={clsx(
                    styles.flowText,
                    !isExpandable && styles.singleLineFlow,
                    isAnimating && styles.animating,
                    !isExpanded && isExpandable && styles.collapsedOneLine,
                    isExpandable && isOneLine && styles.oneLineNowrap,
                    isTruncated && styles.fadeRight,
                )}
                onTransitionEnd={onTransitionEnd}
            >
                {labelElem}
                {valueRegion}
            </Typography>
            <span className={styles.rowActions}>
                {canExpand && (
                    <IconButton
                        size="small"
                        className={styles.expandButton}
                        onClick={toggle}
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
                        data-testid={`expand-fact-${factKey}`}
                    >
                        <ChevronDownIcon
                            size={14}
                            className={clsx(styles.chevron, isExpanded && styles.chevronExpanded)}
                            aria-hidden
                        />
                    </IconButton>
                )}
                {!isPlaceholder && (
                    <CopyTextButton
                        text={copyText ?? trimmedValue}
                        className={styles.copyFactButton}
                        data-testid={`copy-fact-${factKey}`}
                    />
                )}
            </span>
        </div>
    );
}
